import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories } from '@/mocks/categories';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || categories[0].slug);

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeCategory) || categories[0];

  const webPageJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name} - hth官方平台`,
    description: currentCategory.description,
    url: `https://www.hth-platform.com/category/${activeCategory}`,
    isPartOf: {
      '@type': 'WebSite',
      name: 'hth官方平台',
      url: 'https://www.hth-platform.com/',
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageJsonLd) }}
      />
      <ScrollToTop />
      <div className="min-h-screen">
        <Navbar />
        <main className="pt-16">
          <div className="bg-background-100">
            <div className="w-full px-4 md:px-6 py-10 md:py-14">
              <div className="max-w-6xl mx-auto">
                <div className="mb-4">
                  <Breadcrumb
                    items={[
                      { label: '首页', path: '/' },
                      { label: currentCategory.name },
                    ]}
                  />
                </div>

                <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900">
                  {currentCategory.name}
                </h1>
                <p className="mt-2 text-sm md:text-base text-foreground-500 max-w-2xl">
                  {currentCategory.description}
                </p>
              </div>
            </div>
          </div>

          <div className="w-full px-4 md:px-6 py-8">
            <div className="max-w-6xl mx-auto">
              <div className="flex flex-wrap items-center gap-2 mb-8">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    onClick={() => setActiveCategory(cat.slug)}
                    className={`whitespace-nowrap inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer ${
                      activeCategory === cat.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    <i className={`${cat.icon} text-sm`}></i>
                    {cat.name}
                  </Link>
                ))}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {currentCategory.items.map((item) => (
                  <Link
                    key={item.slug}
                    to={`/article/${item.slug}`}
                    className="group p-5 rounded-lg border border-background-200 bg-background-50 hover:border-primary-200 transition-all cursor-pointer"
                  >
                    <div className="w-full aspect-[16/10] rounded-md overflow-hidden bg-background-100">
                      <img
                        src={item.imageUrl}
                        alt={item.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <h3 className="mt-4 text-base font-heading font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2">
                      {item.title}
                    </h3>
                    <p className="mt-2 text-sm text-foreground-500 line-clamp-2">
                      {item.description}
                    </p>
                    {'features' in item && item.features && (
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {item.features.slice(0, 3).map((f) => (
                          <span
                            key={f}
                            className="px-2 py-0.5 text-xs rounded-full bg-primary-50 text-primary-600"
                          >
                            {f}
                          </span>
                        ))}
                      </div>
                    )}
                    {'date' in item && item.date && (
                      <div className="mt-3 text-xs text-foreground-400">{item.date}</div>
                    )}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}